import { type ApolloClient } from "@apollo/client";
import { createApolloClient } from "@package/ui-apollo";
import { useAuthState } from "@package/ui-authentication";
import { useConfigState } from "@package/ui-config";
import {
  CreateEventDocument,
  type CreateEventMutation,
  type CreateEventMutationVariables,
  EventPayloadType,
} from "@package/ui-gql/index-analytics";
import { useOrganizationState } from "@package/ui-organization";
import { useCallback, useEffect, useState } from "react";

type UseReloadLocationParams = (location: string) => Promise<void>;

export function useReloadLocation(
  apolloClient?: ApolloClient<object>,
): UseReloadLocationParams {
  const [client, setClient] = useState<ApolloClient<object>>();

  const { apollo, baseUrlApiAnalytics, environment } = useConfigState();
  const { token, tokenDecoded } = useAuthState();
  const { selectedOrganization } = useOrganizationState();

  const reload = useCallback(
    async (location: string) => {
      if (client) {
        try {
          await client.query<CreateEventMutation, CreateEventMutationVariables>(
            {
              query: CreateEventDocument,
              variables: {
                event: {
                  type: EventPayloadType.CacheReloadRequest,
                  payloadOneOf: {
                    CACHE_RELOAD_REQUEST: {
                      type: EventPayloadType.CacheReloadRequest,
                    },
                  },
                  location,
                },
              },
            },
          );
        } catch (error) {
          console.error("Error reloading location:", error);
        }
      }
    },
    [client],
  );

  useEffect(
    function effectOnApolloClient() {
      const createClient = async () => {
        if (token && tokenDecoded) {
          setClient(
            await createApolloClient({
              ...apollo,
              environment,
              organization: selectedOrganization?.orgId,
              token,
              uri: `${baseUrlApiAnalytics}/api/graphql`,
            }),
          );
        }
      };

      if (apolloClient) {
        setClient(apolloClient);
      } else {
        createClient();
      }
    },
    [
      apollo,
      apolloClient,
      baseUrlApiAnalytics,
      environment,
      selectedOrganization?.orgId,
      token,
      tokenDecoded,
    ],
  );

  return reload;
}
